'use client';

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useConnectorStore } from '@/lib/connectors/store';
import {
  Upload,
  FileJson,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileCode,
} from 'lucide-react';

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  connectorId: string;
}

type ImportSource = 'file' | 'paste';
type ImportFormat = 'json' | 'yaml';

export function ImportDialog({ open, onClose, connectorId }: ImportDialogProps) {
  const { importOpenAPI } = useConnectorStore();

  const [source, setSource] = useState<ImportSource>('file');
  const [format, setFormat] = useState<ImportFormat>('json');
  const [file, setFile] = useState<File | null>(null);
  const [content, setContent] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (droppedFile.name.endsWith('.yaml') || droppedFile.name.endsWith('.yml')) {
        setFormat('yaml');
      }
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!connectorId) {
      setError('Save the connector first before importing');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      let importContent: string;

      if (source === 'file' && file) {
        importContent = await file.text();
      } else if (source === 'paste' && content) {
        importContent = content;
      } else {
        throw new Error('No content to import');
      }

      await importOpenAPI(connectorId, importContent, format);
      setSuccess(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setContent('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="size-5" />
              Import Successful
            </DialogTitle>
            <DialogDescription>
              Operations have been imported from the specification.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import OpenAPI Specification</DialogTitle>
          <DialogDescription>
            Import operations from an OpenAPI/Swagger specification
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!connectorId && (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Save the connector first before importing operations
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {/* Format Selection */}
          <div className="space-y-2">
            <Label>Format</Label>
            <div className="flex gap-4">
              {[
                { value: 'json', label: 'JSON', icon: FileJson },
                { value: 'yaml', label: 'YAML', icon: FileCode },
              ].map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFormat(f.value as ImportFormat)}
                  className={`flex-1 p-4 border rounded-lg text-center transition-colors ${
                    format === f.value
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-muted-foreground/50'
                  }`}
                >
                  <f.icon className="size-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">{f.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Source Selection */}
          <Tabs value={source} onValueChange={(v) => setSource(v as ImportSource)}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="file">
                <Upload className="size-4 mr-2" />
                Upload File
              </TabsTrigger>
              <TabsTrigger value="paste">
                <FileJson className="size-4 mr-2" />
                Paste Content
              </TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="mt-4">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  file ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/50'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                {file ? (
                  <div className="space-y-2">
                    <FileJson className="size-12 mx-auto text-primary" />
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setFile(null)}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="size-12 mx-auto text-muted-foreground" />
                    <p className="text-muted-foreground">
                      Drag and drop a file here, or click to browse
                    </p>
                    <Input
                      type="file"
                      accept=".json,.yaml,.yml"
                      onChange={handleFileSelect}
                      className="max-w-xs mx-auto"
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="paste" className="mt-4">
              <div className="space-y-2">
                <Label htmlFor="import-content">Specification Content</Label>
                <Textarea
                  id="import-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your OpenAPI specification here..."
                  className="font-mono text-sm min-h-[200px]"
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={
              importing ||
              !connectorId ||
              (source === 'file' && !file) ||
              (source === 'paste' && !content)
            }
          >
            {importing ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="size-4 mr-2" />
                Import
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
